import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useSnackbar } from 'notistack';
import { ReadonlyVec3 } from 'gl-matrix';
import { Modality } from '~/domain/dicom/modality';
import { formatPetDisplayValue } from '~/domain/dicom/unitSuffix';
import { Image3D } from '~/domain/patient/image3d';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
} from '~/state/atoms/engineAtoms';
import { secondaryImageState } from '~/state/atoms/image/imageAtoms';
import { petMaxSuvState } from '~/state/atoms/image/petImageAtom';
import { getMissingSUVParams } from '~/domain/patient/petSuv';

export default function usePetImage() {
    const environment = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const setPetMaxSuv = useSetRecoilState(petMaxSuvState);
    const { enqueueSnackbar } = useSnackbar();

    const isPetSecondary = secondaryImage?.firstSlice.modality === Modality.PT;
    const processedImageRef = useRef<Image3D | null>(null);

    const suvFactor = isPetSecondary
        ? (secondaryImage?.suvFactor ?? null)
        : null;
    const maxPixelValue = secondaryImage?.maxValue ?? 1;

    const maxIntensityPosition = useMemo<ReadonlyVec3 | null>(() => {
        if (!secondaryImage || !isPetSecondary) {
            return null;
        }
        return secondaryImage.findMaxIntensityPosition();
    }, [secondaryImage, isPetSecondary]);

    useEffect(() => {
        if (
            !secondaryImage ||
            secondaryImage.firstSlice.modality !== Modality.PT
        ) {
            setPetMaxSuv(null);
            processedImageRef.current = null;
            return;
        }

        if (processedImageRef.current === secondaryImage) {
            return;
        }
        processedImageRef.current = secondaryImage;

        if (suvFactor !== null) {
            const maxSuv = maxPixelValue * suvFactor;
            setPetMaxSuv(Math.round(maxSuv * 100) / 100);
        } else {
            setPetMaxSuv(null);

            const petMetadata = secondaryImage.firstSlice.petMetadata;
            const missingParams = petMetadata
                ? getMissingSUVParams(petMetadata)
                : ['PET metadata'];

            enqueueSnackbar(
                `SUVbw values cannot be computed. Missing DICOM parameters: ${missingParams.join(', ')}. Displaying raw intensity (kBq/ml).`,
                {
                    variant: 'warning',
                    autoHideDuration: 8000,
                    preventDuplicate: true,
                }
            );
        }
    }, [
        secondaryImage,
        suvFactor,
        maxPixelValue,
        setPetMaxSuv,
        enqueueSnackbar,
    ]);

    const formatPetValue = useCallback(
        (rawValue: number): string =>
            formatPetDisplayValue(rawValue, suvFactor),
        [suvFactor]
    );

    const formatNormalizedPetValue = useCallback(
        (normalized: number): string =>
            formatPetDisplayValue(normalized * maxPixelValue, suvFactor),
        [maxPixelValue, suvFactor]
    );

    const navigateToMaxIntensity = useCallback(() => {
        if (
            !environment ||
            !mainView ||
            !upperSideView ||
            !lowerSideView ||
            !maxIntensityPosition
        ) {
            return;
        }

        environment.panZoom.focusToPoint(
            mainView,
            upperSideView,
            lowerSideView,
            maxIntensityPosition
        );
    }, [
        environment,
        mainView,
        upperSideView,
        lowerSideView,
        maxIntensityPosition,
    ]);

    const maxIntensityLabel = formatPetDisplayValue(maxPixelValue, suvFactor);

    return {
        isPetSecondary,
        suvFactor,
        maxPixelValue,
        maxIntensityLabel,
        formatPetValue,
        formatNormalizedPetValue,
        navigateToMaxIntensity,
    };
}
